import type { ChangeEvent, JSX } from 'react';

interface RangeSliderGroupProps {
  title: string;
  description: string;
  minLabel: string;
  maxLabel: string;
  minDisplay: string;
  maxDisplay: string;
  minValue: number;
  maxValue: number;
  minBound: number;
  maxBound: number;
  disabled: boolean;
  onMinChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onMaxChange: (event: ChangeEvent<HTMLInputElement>) => void;
  showMinHandle?: boolean;
  rangeLabel?: string;
  error?: string | null;
  errorId?: string;
}

export function RangeSliderGroup({
  title,
  description,
  minLabel,
  maxLabel,
  minDisplay,
  maxDisplay,
  minValue,
  maxValue,
  minBound,
  maxBound,
  disabled,
  onMinChange,
  onMaxChange,
  showMinHandle = true,
  rangeLabel,
  error,
  errorId,
}: RangeSliderGroupProps): JSX.Element {
  const hasError = Boolean(error);
  const describedBy = hasError ? errorId : undefined;
  const hasRangeLabel = typeof rangeLabel === 'string' && rangeLabel.trim().length > 0;
  const minOnTop = showMinHandle && minValue >= maxValue - 1;
  const sliderBaseClass =
    'absolute inset-0 h-2 w-full cursor-pointer accent-brand-primary disabled:cursor-not-allowed disabled:opacity-50';
  const stackedSliderClass = showMinHandle ? 'range-slider-input' : '';

  return (
    <div className="rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">{title}</p>
      <p className="mt-1 text-sm text-text-primary/70">{description}</p>
      <div className="mt-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-text-primary/60">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
      <div className="mt-1 flex items-center justify-between text-sm font-semibold text-brand-primary">
        <span>{minDisplay}</span>
        <span>{maxDisplay}</span>
      </div>
      <div className="relative mt-3 h-2">
        {showMinHandle ? (
          <input
            type="range"
            min={minBound}
            max={maxBound}
            step="1"
            value={minValue}
            onChange={onMinChange}
            aria-label={minLabel}
            aria-valuetext={minDisplay}
            aria-invalid={hasError ? true : undefined}
            aria-describedby={describedBy}
            disabled={disabled}
            className={`${sliderBaseClass} ${stackedSliderClass} ${minOnTop ? 'z-20' : 'z-10'}`}
          />
        ) : null}
        <input
          type="range"
          min={minBound}
          max={maxBound}
          step="1"
          value={maxValue}
          onChange={onMaxChange}
          aria-label={maxLabel}
          aria-valuetext={maxDisplay}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          className={`${sliderBaseClass} ${stackedSliderClass} ${minOnTop ? 'z-10' : 'z-20'}`}
        />
      </div>
      {hasRangeLabel ? (
        <p className="mt-2 text-[0.7rem] font-semibold uppercase tracking-wide text-text-primary/50">
          Range {rangeLabel}
        </p>
      ) : null}
      {hasError ? (
        <p id={errorId} className="mt-2 text-xs font-semibold text-rose-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
